<template>
  <AuthenticatedView
    :loading="userLoading && !userResult"
    :current-user="currentUser"
    :has-result="!!userResult"
  >
    <div>
      <AppHeader
        v-if="!embedded"
        :user="currentUser"
        :dashboard-url="dashboardUrl"
        :profile-url="profileUrl"
        :chat-id="chatId"
        :from-id="fromId"
      />

      <div :class="embedded ? 'dashboard-panel' : 'card'">
        <breadcrumbs v-if="!embedded" :items="breadcrumbsItems" />
        <div
          :class="embedded ? 'dashboard-panel-header' : ''"
          :style="
            embedded
              ? ''
              : 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px;'
          "
        >
          <h3 v-if="embedded" id="attachments-panel-title" class="dashboard-panel-title">
            {{ s__('Attachments|My files') }}
          </h3>
          <h2 v-else style="margin: 0">{{ s__('Attachments|My Attachments') }}</h2>
          <button
            v-if="isTelegramWebView()"
            type="button"
            class="webview-warning-btn-header"
            :title="
              s__(
                'Attachments|File attachments download may not properly work in WebView. Click for more information.'
              )
            "
            @click="showWebViewInfoModal = true"
          >
            <WarningIcon :size="18" />
          </button>
        </div>

        <div :class="{ 'dashboard-panel-body': embedded }">
          <div v-if="loading && !attachments.length" class="loading-state">
            {{ __('Loading…') }}
          </div>

          <div v-else-if="error" class="error">{{ error }}</div>

          <div v-else>
            <div v-if="availableProjects.length" class="user-upload-section">
              <h4 class="user-upload-title">{{ s__('Attachments|Upload files') }}</h4>
              <label class="user-upload-project-label">
                <span>{{ s__('Attachments|Project') }}</span>
                <select v-model="selectedProjectId" class="user-upload-project-select">
                  <option value="">{{ s__('Attachments|Select project') }}</option>
                  <option
                    v-for="project in availableProjects"
                    :key="project.id"
                    :value="project.id"
                  >
                    {{ project.title }}
                  </option>
                </select>
              </label>
              <p class="user-upload-hint">
                {{ s__('Attachments|Choose a project before uploading files.') }}
              </p>
              <FileUploader
                v-if="selectedProjectId"
                attachable-type="Project"
                :attachable-id="selectedProjectId"
                :project-id="selectedProjectId"
                @uploaded="handleUserFilesUploaded"
                @error="handleUploadError"
              />
            </div>
            <div v-else class="attachments-empty-hint user-upload-empty">
              <span class="attachments-empty-hint-icon"><InfoIcon /></span>
              <p class="attachments-empty-hint-title">
                {{ s__('Attachments|No active projects available for uploads.') }}
              </p>
            </div>

            <!-- Attachments List -->
            <div v-if="attachments.length" class="attachments-list-section">
              <div class="attachments-header">
                <div class="attachments-header-left">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="paperclip-icon"
                  >
                    <path
                      d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551"
                    ></path>
                  </svg>
                  <span>{{ s__('Attachments|Attachments') }} ({{ totalCount }})</span>
                </div>
              </div>

              <AttachmentItemsList
                :attachments="attachments"
                :downloading="downloading"
                :sending-to-telegram="sendingToTelegram"
                :get-file-icon-class="getFileIconClass"
                :get-file-icon="getFileIcon"
                :is-image-attachment="isImageAttachment"
                :get-task-url="getTaskUrl"
                @view-image="openImageGallery"
                @download="handleDownload"
                @send-to-telegram="handleSendToTelegram"
                @delete="confirmDelete"
                @public-link-updated="handlePublicLinkUpdated"
              />

              <!-- Load More Button -->
              <div v-if="hasNextPage" class="load-more-section">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="loadingMore"
                  @click="loadMore"
                >
                  <span v-if="loadingMore">{{ __('Loading…') }}</span>
                  <span v-else>{{ s__('Attachments|Load more') }}</span>
                </button>
              </div>
            </div>

            <div v-else class="attachments-empty-hint">
              <span class="attachments-empty-hint-icon"><InfoIcon /></span>
              <p class="attachments-empty-hint-title">{{ s__('Attachments|No files yet.') }}</p>
              <p class="attachments-empty-hint-text">
                {{
                  s__(
                    'Attachments|Add files from task details or comments using the attachment upload control.'
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :visible="showConfirmModal"
      :title="s__('Attachments|Delete Attachment?')"
      :message="
        s__(
          'Attachments|Are you sure you want to delete this attachment? This action cannot be undone.'
        )
      "
      :confirm-text="s__('Attachments|Delete')"
      :cancel-text="s__('Attachments|Cancel')"
      @confirm="deleteAttachment"
      @cancel="cancelDelete"
    />

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      :visible="showImageGallery"
      :images="galleryImages"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />

    <AlertBanner
      v-if="downloadError"
      type="error"
      :title="s__('Attachments|Download Failed')"
      :message="downloadError"
      :dismissible="true"
      :duration="0"
      @dismiss="downloadError = null"
    />

    <AlertBanner
      v-if="sendToTelegramInfo"
      type="info"
      :title="s__('Attachments|Sending to Telegram')"
      :message="
        s__(
          'Attachments|Your attachment is being sent to your Telegram Direct Messages. This may take a few moments.'
        )
      "
      :dismissible="true"
      :duration="5000"
      @dismiss="sendToTelegramInfo = false"
    />

    <AlertBanner
      v-if="sendToTelegramError"
      type="error"
      :title="s__('Attachments|Send to Telegram Failed')"
      :message="sendToTelegramError"
      :dismissible="true"
      :duration="0"
      @dismiss="sendToTelegramError = null"
    />

    <!-- WebView Info Modal -->
    <AttachmentsWebViewInfoModal
      :visible="showWebViewInfoModal"
      @close="showWebViewInfoModal = false"
    />
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, loadTranslations } from '@/locale'
import AlertBanner from '../common/AlertBanner.vue'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import WarningIcon from '../common/icons/WarningIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import ConfirmModal from '../common/ConfirmModal.vue'
import ImageGalleryModal from '../common/ImageGalleryModal.vue'
import AttachmentsWebViewInfoModal from '../common/AttachmentsWebViewInfoModal.vue'
import AttachmentItemsList from '../common/AttachmentItemsList.vue'
import FileUploader from '../common/FileUploader.vue'
import AppHeader from '../common/AppHeader.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { useAttachmentList } from '@/composables/useAttachmentList'
import { useAttachmentDownload } from '@/composables/useAttachmentDownload'

const props = defineProps({
  chatId: String,
  fromId: String,
  embedded: {
    type: Boolean,
    default: false,
  },
})

const loading = ref(true)
const error = ref('')
const attachments = ref([])
const availableProjects = ref([])
const selectedProjectId = ref('')
const totalCount = ref(0)
const hasNextPage = ref(false)
const endCursor = ref(null)
const loadingMore = ref(false)
const localeKey = ref(0)

// Download composable for iOS support
const { downloading, downloadError, downloadAttachment, isTelegramWebView } =
  useAttachmentDownload()

// Send to Telegram state
const sendingToTelegram = ref(false)
const sendToTelegramError = ref(null)
const sendToTelegramInfo = ref(false)

// WebView info modal state
const showWebViewInfoModal = ref(false)

// Use composable for shared attachment list logic
const {
  showConfirmModal,
  showImageGallery,
  galleryImages,
  galleryInitialIndex,
  isImageAttachment,
  openImageGallery: openImageGalleryBase,
  closeImageGallery,
  confirmDelete,
  cancelDelete,
  deleteAttachment: deleteAttachmentBase,
  getFileIconClass,
  getFileIcon,
  getTaskUrl: getTaskUrlBase,
} = useAttachmentList({
  onDelete: deletedAttachment => {
    // Remove from local list
    const index = attachments.value.findIndex(a => a.id === deletedAttachment.id)
    if (index !== -1) {
      attachments.value.splice(index, 1)
      totalCount.value = Math.max(0, totalCount.value - 1)
    }
  },
})

// Wrapper methods that use the composable with component-specific data
const openImageGallery = attachment => {
  openImageGalleryBase(attachment, attachments.value)
}

const SEND_TO_TELEGRAM_MUTATION = gql`
  mutation SendAttachmentToTelegram($id: ID!) {
    sendAttachmentToTelegram(id: $id) {
      success
      errors
    }
  }
`

const { mutate: sendToTelegramMutation } = useMutation(SEND_TO_TELEGRAM_MUTATION)

const handleDownload = async attachment => {
  await downloadAttachment(attachment.downloadUrl, attachment.filename)
}

const handleSendToTelegram = async attachment => {
  if (!attachment) return

  sendingToTelegram.value = true
  sendToTelegramError.value = null
  sendToTelegramInfo.value = false

  try {
    const { data } = await sendToTelegramMutation({ id: attachment.id })

    if (data?.sendAttachmentToTelegram?.errors?.length) {
      // Error occurred (rate limit, missing file, authorization, etc.) - show error immediately
      const errorMsg = data.sendAttachmentToTelegram.errors.join(', ')
      sendToTelegramError.value = s__('Attachments|Failed to send to Telegram: %{error}').replace(
        '%{error}',
        errorMsg
      )
      sendToTelegramInfo.value = false
    } else if (data?.sendAttachmentToTelegram?.success) {
      // Job was successfully enqueued - show info banner
      sendToTelegramInfo.value = true
      // Hide info banner after a short delay
      setTimeout(() => {
        sendToTelegramInfo.value = false
      }, 5000)
    }
  } catch (error) {
    console.error('Send to Telegram error:', error)
    sendToTelegramError.value = s__('Attachments|Failed to send to Telegram')
    sendToTelegramInfo.value = false
  } finally {
    sendingToTelegram.value = false
  }
}

const deleteAttachment = async () => {
  try {
    await deleteAttachmentBase()
  } catch (err) {
    error.value = err.message || s__('Attachments|Failed to delete attachment')
  }
}

const handleUserFilesUploaded = async () => {
  await refetchCurrentAttachments()
}

const handleUploadError = errors => {
  error.value = Array.isArray(errors) ? errors.join(', ') : String(errors)
}

const getTaskUrl = task => {
  return getTaskUrlBase(task, props.chatId, props.fromId)
}

const handlePublicLinkUpdated = ({ attachmentId, publicLink }) => {
  const attachment = attachments.value.find(item => item.id === attachmentId)
  if (attachment) {
    attachment.publicLink = publicLink
  }
}

// Get viewer's Telegram user ID
const viewerUid = (() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  return (tg && tg.user && tg.user.id) || props.fromId || null
})()

// GraphQL queries
const USER_Q_TELEGRAM = gql`
  query GetUserAttachmentsByTelegram($uid: BigInt!, $first: Int, $after: String) {
    userByTelegram(telegramUserId: $uid) {
      id
      projects(first: 25) {
        edges {
          node {
            id
            title
            archived
          }
        }
      }
      attachments(first: $first, after: $after) {
        edges {
          node {
            id
            filename
            byteSize
            contentType
            createdAt
            downloadUrl
            humanSize
            canCreatePublicLink
            canManagePublicLink
            publicLink {
              id
              expiresAt
              passwordProtected
              publicUrl
              revoked
              expired
            }
            parentTask {
              id
              iid
              title
              projectId
            }
            parentTasks {
              id
              iid
              title
              projectId
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    currentUser {
      id
      name
      username
      projects(first: 25) {
        edges {
          node {
            id
            title
            archived
          }
        }
      }
    }
  }
`

const USER_Q_CURRENT = gql`
  query GetCurrentUserAttachments($first: Int, $after: String) {
    currentUser {
      id
      name
      username
      projects(first: 25) {
        edges {
          node {
            id
            title
            archived
          }
        }
      }
      attachments(first: $first, after: $after) {
        edges {
          node {
            id
            filename
            byteSize
            contentType
            createdAt
            downloadUrl
            humanSize
            canCreatePublicLink
            canManagePublicLink
            publicLink {
              id
              expiresAt
              passwordProtected
              publicUrl
              revoked
              expired
            }
            parentTask {
              id
              iid
              title
              projectId
            }
            parentTasks {
              id
              iid
              title
              projectId
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

// Use currentUser query when there's no Telegram context (browser authentication)
// Otherwise use userByTelegram query
const useCurrentUserQuery = computed(() => !viewerUid)

const {
  result: userResultTelegram,
  loading: userLoadingTelegram,
  error: userErrorTelegram,
  fetchMore: fetchMoreTelegram,
  refetch: refetchTelegram,
} = useQuery(USER_Q_TELEGRAM, () => ({ uid: Number(viewerUid), first: 50, after: null }), {
  enabled: computed(() => !!viewerUid && !useCurrentUserQuery.value),
  fetchPolicy: 'network-only',
})

const {
  result: userResultCurrent,
  loading: userLoadingCurrent,
  error: userErrorCurrent,
  fetchMore: fetchMoreCurrent,
  refetch: refetchCurrent,
} = useQuery(USER_Q_CURRENT, () => ({ first: 50, after: null }), {
  enabled: computed(() => useCurrentUserQuery.value),
  fetchPolicy: 'network-only',
})

// Combine results
const userResult = computed(() =>
  useCurrentUserQuery.value ? userResultCurrent.value : userResultTelegram.value
)
const userLoading = computed(() =>
  useCurrentUserQuery.value ? userLoadingCurrent.value : userLoadingTelegram.value
)
const userError = computed(() =>
  useCurrentUserQuery.value ? userErrorCurrent.value : userErrorTelegram.value
)

const refetchCurrentAttachments = async () => {
  const refetchFn = useCurrentUserQuery.value ? refetchCurrent : refetchTelegram
  await refetchFn({
    first: 50,
    after: null,
    ...(useCurrentUserQuery.value ? {} : { uid: Number(viewerUid) }),
  })
}

// Current user for AppHeader
// Current user for AppHeader (the logged-in user, not the user whose attachments we're viewing)
const currentUser = computed(() => {
  return userResult.value?.currentUser
})

watch(
  userLoading,
  val => {
    loading.value = val
    // If loading is false and we have a result, remove the initial loading screen
    if (!val && userResult.value) {
      removeInitialLoading()
    }
  },
  { immediate: true }
)

watch(loading, isLoading => {
  if (!isLoading && userResult.value) {
    removeInitialLoading()
  }
})

watch(
  userError,
  val => {
    if (val) {
      console.error('UserAttachments query error:', val)
      error.value = val.message || 'Failed to load attachments'
      removeInitialLoading()
    }
  },
  { immediate: true }
)

// Debug: log query state to help diagnose infinite loader
watch(
  [userLoading, userResult, userError],
  ([loading, result, err]) => {
    console.log('UserAttachments query state:', {
      loading,
      hasResult: !!result,
      hasError: !!err,
      viewerUid,
      useCurrentUserQuery: useCurrentUserQuery.value,
      userData: result?.userByTelegram || result?.currentUser,
    })
  },
  { immediate: true }
)

watch(
  userResult,
  data => {
    if (data) {
      // We have a result, remove loading screen
      removeInitialLoading()
    }

    const userData = data?.userByTelegram || data?.currentUser
    if (userData) {
      availableProjects.value = (userData.projects?.edges || [])
        .map(edge => edge.node)
        .filter(project => project && !project.archived)

      if (
        selectedProjectId.value &&
        !availableProjects.value.some(project => project.id === selectedProjectId.value)
      ) {
        selectedProjectId.value = ''
      }

      const attachmentsData = userData.attachments
      if (attachmentsData) {
        attachments.value = attachmentsData.edges.map(edge => edge.node)
        hasNextPage.value = attachmentsData.pageInfo.hasNextPage
        endCursor.value = attachmentsData.pageInfo.endCursor
        // Show loaded count (totalCount is not available on connection type)
        totalCount.value = attachments.value.length

        // Load translations for the user's preferred language
        if (userData.preferredLanguage) {
          document.documentElement.lang = userData.preferredLanguage
          loadTranslations(userData.preferredLanguage)
          localeKey.value++ // Force re-render
        }
      }
    }
  },
  { immediate: true }
)

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const profileUrl = computed(() => {
  let url = '/profile'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  localeKey.value // eslint-disable-line no-unused-expressions
  return [
    { label: s__('Dashboard|Main'), url: dashboardUrl.value },
    { label: s__('Profile|Profile'), url: profileUrl.value },
    { label: s__('Attachments|My Attachments') },
  ]
})

const loadMore = async () => {
  if (loadingMore.value || !hasNextPage.value || !endCursor.value) return

  loadingMore.value = true
  try {
    const fetchMoreFn = useCurrentUserQuery.value ? fetchMoreCurrent : fetchMoreTelegram
    const result = await fetchMoreFn({
      variables: {
        first: 50,
        after: endCursor.value,
        ...(useCurrentUserQuery.value ? {} : { uid: Number(viewerUid) }),
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousUser = previousResult.userByTelegram || previousResult.currentUser
        const moreUser = fetchMoreResult.userByTelegram || fetchMoreResult.currentUser

        if (!moreUser?.attachments) return previousResult

        const previousEdges = previousUser.attachments.edges || []
        const newEdges = moreUser.attachments.edges || []
        const pageInfo = moreUser.attachments.pageInfo

        const updated = useCurrentUserQuery.value
          ? {
              ...previousResult,
              currentUser: {
                ...previousUser,
                attachments: {
                  ...moreUser.attachments,
                  edges: [...previousEdges, ...newEdges],
                  pageInfo,
                },
              },
            }
          : {
              ...previousResult,
              userByTelegram: {
                ...previousUser,
                attachments: {
                  ...moreUser.attachments,
                  edges: [...previousEdges, ...newEdges],
                  pageInfo,
                },
              },
            }

        // Update local state from the merged result
        const userData = updated.userByTelegram || updated.currentUser
        if (userData?.attachments) {
          attachments.value = userData.attachments.edges.map(edge => edge.node)
          hasNextPage.value = userData.attachments.pageInfo?.hasNextPage || false
          endCursor.value = userData.attachments.pageInfo?.endCursor || null
          // Show loaded count (totalCount is not available on connection type)
          totalCount.value = attachments.value.length
        }

        return updated
      },
    })
  } catch (err) {
    console.error('Failed to load more attachments:', err)
    error.value = s__('Attachments|Failed to load more attachments')
  } finally {
    loadingMore.value = false
  }
}
</script>

<style src="../../styles/attachments_list.css"></style>
<style scoped>
.card {
  max-width: 1200px;
  margin: 24px auto;
  padding: 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.error {
  color: #dc2626;
  font-size: 14px;
  padding: 16px;
  background: #fee2e2;
  border-radius: 8px;
  margin-bottom: 16px;
}

.attachments-list-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.user-upload-section {
  display: grid;
  gap: 10px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
}

.user-upload-title {
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 700;
}

.user-upload-project-label {
  display: grid;
  gap: 6px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.user-upload-project-select {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
}

.user-upload-hint {
  margin: 0;
  color: #666;
  font-size: 13px;
}

.user-upload-empty {
  margin-bottom: 16px;
}

.attachments-empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #dbeafe;
  color: #1e3a8a;
}

.attachments-empty-hint-icon {
  width: 20px;
  height: 20px;
}

.attachments-empty-hint-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.attachments-empty-hint-text {
  margin: 0;
  font-size: 13px;
  text-align: center;
}

.load-more-section {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.webview-warning-btn-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  color: #92400e;
  cursor: pointer;
  transition: all 0.2s;
}

.webview-warning-btn-header:hover {
  background: #fde68a;
  border-color: #f59e0b;
}

.webview-warning-btn-header:active {
  background: #fcd34d;
}

.webview-warning-btn-header svg {
  color: #d97706;
}
</style>
